import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ErreurApi, type FicheTache, requetes } from '../api'
import { BoutonMail, type FichierVu, Visionneuse } from './ApercuPiece'
import { formaterInstant } from './Historique'
import { PuceAdresse, PucesAdresses } from './PucesAdresses'

// Le mail d'une tâche, lu à la demande dans Gmail et montré dans sa carte : son texte et
// ses pièces jointes. Rien n'en est gardé. Le HTML s'affiche dans un cadre isolé : ni
// script, ni formulaire, ni ressource extérieure (pas de pixel espion), liens dans un
// nouvel onglet.

type MailTache = FicheTache['mails'][number]

/** Hauteur au-delà de laquelle le mail est replié. */
const HAUTEUR_REPLIEE = 420

const POLITIQUE =
  "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; form-action 'none'"

function documentIsole(html: string) {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${POLITIQUE}"><base target="_blank"><style>html{color-scheme:light}body{margin:12px;font:14px/1.5 system-ui,sans-serif;color:#16201a;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${html}</body></html>`
}

/** Le cadre prend la hauteur du mail (lisible sans script : même origine, scripts bloqués). */
function CadreHtml({ html, onHauteur }: { html: string; onHauteur: (h: number) => void }) {
  const cadre = useRef<HTMLIFrameElement>(null)
  const [hauteur, setHauteur] = useState(200)
  const signaler = useRef(onHauteur)
  signaler.current = onHauteur
  const mesurer = () => {
    const h = cadre.current?.contentDocument?.documentElement.scrollHeight
    if (!h) return
    setHauteur(h)
    signaler.current(h)
  }
  useEffect(() => {
    window.addEventListener('resize', mesurer)
    return () => window.removeEventListener('resize', mesurer)
  }, [])
  return (
    <iframe
      ref={cadre}
      className="mail-cadre"
      title="Contenu du mail"
      // allow-same-origin sans allow-scripts : la hauteur se lit, aucun script ne tourne.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={documentIsole(html)}
      onLoad={mesurer}
      style={{ height: hauteur }}
    />
  )
}

const taille = (octets: number) =>
  octets >= 1_000_000
    ? `${(octets / 1_000_000).toFixed(1).replace('.', ',')} Mo`
    : `${Math.max(1, Math.round(octets / 1000))} ko`

function CorpsMail({ id }: { id: string }) {
  const mail = useQuery(requetes.mail(id))
  const [deplie, setDeplie] = useState(false)
  const [haut, setHaut] = useState(false)
  const [vu, setVu] = useState<FichierVu | null>(null)
  const texte = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (texte.current) setHaut(texte.current.scrollHeight > HAUTEUR_REPLIEE)
  }, [mail.data])

  if (mail.isPending) return <p className="discret">Lecture du mail…</p>
  if (mail.isError)
    return (
      <p className="discret">
        {mail.error instanceof ErreurApi ? mail.error.message : 'Mail illisible ici.'}
      </p>
    )
  const m = mail.data
  const url = (partie: string) => `/api/mails/${id}/pieces-jointes/${partie}`
  return (
    <div className="mail-lu">
      {(m.a || m.cc) && (
        <div className="discret mail-destinataires">
          {m.a && (
            <span className="mail-destinataires-groupe">
              à <PucesAdresses entete={m.a} />
            </span>
          )}
          {m.cc && (
            <span className="mail-destinataires-groupe">
              cc <PucesAdresses entete={m.cc} />
            </span>
          )}
        </div>
      )}
      <div
        className={`mail-corps${haut && !deplie ? ' replie' : ''}`}
        style={haut && !deplie ? { maxHeight: HAUTEUR_REPLIEE } : undefined}
      >
        {m.html ? (
          <CadreHtml html={m.html} onHauteur={(h) => setHaut(h > HAUTEUR_REPLIEE)} />
        ) : (
          <div ref={texte} className="mail-texte">
            {m.texte ?? 'Mail sans texte.'}
          </div>
        )}
      </div>
      {haut && (
        <button type="button" className="bouton-discret" onClick={() => setDeplie(!deplie)}>
          {deplie ? 'Replier le mail' : 'Afficher tout le mail'}
        </button>
      )}
      {m.piecesJointes.length > 0 && (
        <div className="discret mail-intitule">Pièces jointes du mail</div>
      )}
      {m.piecesJointes.length > 0 && (
        <ul className="mail-pieces-jointes" aria-label="Pièces jointes">
          {m.piecesJointes.map((p) => (
            <li key={p.partie}>
              <button
                type="button"
                className="mail-piece-jointe"
                onClick={() => setVu({ url: url(p.partie), nom: p.nom, typeMime: p.typeMime })}
              >
                <strong>{p.nom}</strong>
                <span className="discret">{taille(p.taille)}</span>
              </button>
              <a
                className="bouton-discret mail-enregistrer"
                href={url(p.partie)}
                download={p.nom}
                aria-label={`Enregistrer ${p.nom}`}
              >
                Enregistrer
              </a>
            </li>
          ))}
        </ul>
      )}
      {vu && <Visionneuse f={vu} onFermer={() => setVu(null)} />}
    </div>
  )
}

/** Un mail d'une tâche : en-tête, bouton vers Gmail, puis son contenu et ses pièces jointes. */
export function MailLu({ m }: { m: MailTache }) {
  const entete = [m.expediteur, m.recuLe ? `reçu le ${formaterInstant(m.recuLe)}` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="mail">
      <div className="ligne">
        <div className="ligne-contenu">
          <strong>{m.objet ?? 'Mail sans objet'}</strong>
          <div className="discret">
            {entete}
            {!m.aMoi && (
              <>
                {entete ? ' · boîte ' : 'boîte '}
                <PuceAdresse nom={null} adresse={m.adresse} />
              </>
            )}
          </div>
        </div>
        <div className="ligne-actions">
          <BoutonMail lien={m.lien} discret={!m.aMoi} />
        </div>
      </div>
      <CorpsMail id={m.id} />
    </div>
  )
}
